/**
 * @fileoverview Upload overlay UI component for displaying file upload progress.
 *
 * This component provides a visual overlay that shows:
 * - Active uploads with progress indicators
 * - Queued uploads waiting to start
 * - Failed uploads with retry functionality
 * - Cancellation confirmation dialogs
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HighlightOffRounded,
  AttachFile,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  keyframes,
  styled,
  useTheme,
  Divider,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Trash } from 'src/components/icons/trash';
import { UploadState, UploadStatus, useUpload } from 'src/hooks/use_upload';
import { UploadedFileCategory } from 'src/types';

// Styled Components
const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const OverlayContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  right: theme.spacing(3),
  zIndex: theme.zIndex.snackbar,
  animation: `${slideUp} 0.3s ease-out`,
  maxWidth: '412px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: '10px 24px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  borderTop: `3px solid ${theme.palette.primary.main}`,
}));

// Upload Card Component
interface UploadCardProps {
  upload: UploadState;
  onCancel: (fileId: string) => void;
  onRetry: (fileId: string) => Promise<void>;
}

const UploadRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1, 0),
  minHeight: '64px',
}));

const IconContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
});

const FileInfoContainer = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const ActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const UploadCardComponent: React.FC<UploadCardProps> = ({
  upload,
  onCancel,
  onRetry,
}) => {
  const theme = useTheme();

  const getStatusText = () => {
    switch (upload.status) {
      case UploadStatus.InProgress:
        return 'Uploading...';
      case UploadStatus.Failed:
        return 'Upload failed';
      case UploadStatus.Queued:
        return upload.fileId.startsWith('temp-')
          ? 'Uploading...'
          : 'Queued for upload';
      case UploadStatus.Completed:
        return 'Upload completed';
      case UploadStatus.Canceled:
        return 'Upload canceled';
      default:
        return upload.status;
    }
  };

  const getStatusColor = () => {
    switch (upload.status) {
      case UploadStatus.Failed:
        return theme.palette.error.main;
      case UploadStatus.Completed:
        return theme.palette.success.main;
      case UploadStatus.InProgress:
        return theme.palette.text.secondary;
      default:
        return theme.palette.text.secondary;
    }
  };

  return (
    <UploadRow>
      <IconContainer>
        <AttachFile
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '24px',
          }}
        />
        {upload.status === UploadStatus.InProgress && (
          <CircularProgress
            variant='determinate'
            value={upload.progress}
            size={40}
            thickness={2}
            sx={{
              position: 'absolute',
              color: theme.palette.primary.main,
            }}
          />
        )}
      </IconContainer>
      <FileInfoContainer>
        <Typography
          variant='body2'
          noWrap
          sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
        >
          {upload.fileName}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant='caption' sx={{ color: getStatusColor() }}>
            {getStatusText()}
          </Typography>
          {upload.status === UploadStatus.Failed && (
            <Button
              variant='text'
              size='small'
              onClick={() => onRetry(upload.fileId)}
              sx={{
                fontSize: theme.typography.caption.fontSize,
                padding: '2px 6px',
                height: '24px',
              }}
            >
              Retry
            </Button>
          )}
        </Box>
      </FileInfoContainer>
      <ActionsContainer>
        <Button
          component={Link}
          to={upload.orderUrl}
          variant='text'
          size='small'
          sx={{
            fontSize: theme.typography.caption.fontSize,
            padding: '2px 6px',
            height: '24px',
            color: theme.palette.primary.main,
          }}
        >
          {upload.crossOrgId}
        </Button>

        {(upload.status === UploadStatus.InProgress ||
          upload.status === UploadStatus.Queued ||
          upload.status === UploadStatus.Failed) && (
          <IconButton
            size='small'
            onClick={() => onCancel(upload.fileId)}
            sx={{ color: theme.palette.text.secondary }}
          >
            <HighlightOffRounded fontSize='small' />
          </IconButton>
        )}
      </ActionsContainer>
    </UploadRow>
  );
};

// Helper function to display uploads with active upload first
const getUniqueUploadsForDisplay = (uploads: UploadState[]): UploadState[] => {
  // Sort uploads by status priority
  const statusPriority = {
    [UploadStatus.InProgress]: 0, // Active uploads first
    [UploadStatus.Failed]: 1,
    [UploadStatus.Queued]: 2,
    [UploadStatus.Completed]: 3,
    [UploadStatus.Canceled]: 4,
  };

  return uploads.sort(
    (a, b) => statusPriority[a.status] - statusPriority[b.status],
  );
};

// Upload Overlay Manager Component
export const UploadOverlayManager: React.FC = () => {
  const theme = useTheme();
  const { activeUpload, queue, removeFileUpload, retryUpload } = useUpload();
  const [isExpanded, setIsExpanded] = useState(true);
  const [cancelConfirmation, setCancelConfirmation] = useState<{
    isOpen: boolean;
    fileId: string | null;
  }>({ isOpen: false, fileId: null });

  // Combine active upload and queue for display; only show slide image uploads
  const allUploads = activeUpload ? [activeUpload, ...queue] : queue;
  const slideImageUploads = allUploads.filter(
    (u) => u.category === UploadedFileCategory.SlideImage,
  );
  const uniqueUploads = getUniqueUploadsForDisplay(slideImageUploads);

  if (uniqueUploads.length === 0) {
    return null;
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCancelClick = (fileId: string) => {
    setCancelConfirmation({ isOpen: true, fileId });
  };

  const handleConfirmCancel = () => {
    if (cancelConfirmation.fileId) {
      const upload = allUploads.find(
        (u) => u.fileId === cancelConfirmation.fileId,
      );
      if (upload?.orderId) {
        removeFileUpload(cancelConfirmation.fileId, upload.orderId);
      }
    }
    setCancelConfirmation({ isOpen: false, fileId: null });
  };

  const handleCancelDialog = () => {
    setCancelConfirmation({ isOpen: false, fileId: null });
  };

  return (
    <OverlayContainer>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant='body1'
          color={theme.palette.text.primary}
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          File Uploading...
        </Typography>
        <IconButton
          size='small'
          onClick={toggleExpanded}
          sx={{ color: theme.palette.text.secondary }}
        >
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      {isExpanded && (
        <>
          <Divider sx={{ mx: '-24px' }} />
          {uniqueUploads.map((upload) => (
            <UploadCardComponent
              key={upload.fileId}
              upload={upload}
              onCancel={handleCancelClick}
              onRetry={retryUpload}
            />
          ))}
        </>
      )}
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          title: 'Confirm Action!',
          subtitle: 'Are you sure you want to cancel this slide image upload?',
          type: FeedbackType.Error,
          icon: Trash,
        }}
        isDialogOpen={cancelConfirmation.isOpen}
        onPrimaryButtonClick={handleConfirmCancel}
        onSecondaryButtonClick={handleCancelDialog}
        handleDialogClose={handleCancelDialog}
      />
    </OverlayContainer>
  );
};
